import { SearchField, Skeleton, Text, TextButton } from '@oneaudi/unified-web-alpha-components';
import { htmlToText } from 'html-to-text';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardSkeleton } from '~/app/compositions/sidebar/accessories-equiptments/components/card-skeleton';
import { Categories, SelectionOption } from '~/app/compositions/sidebar/accessories-equiptments/components/categories';
import { ListSkeleton } from '~/app/compositions/sidebar/accessories-equiptments/components/list-skeleton';
import { NoResult } from '~/app/compositions/sidebar/accessories-equiptments/components/no-result';
import {
	AccessoriesConfigurationStepStyle,
	AccessoriesSelectionStepStyle,
	StyledAccessoriesWrapper,
} from '~/app/compositions/sidebar/accessories-equiptments/styles/accessories-equipment-style';
import { OptionsHeader } from '~/app/compositions/sidebar/options-header';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { ACCESSORIES_MIN_VISIBLE_ITEMS } from '~/app/models/constans';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { showMoreInformationDialog } from '~/app/pages/configurator/models/show-more-information-dialog';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { ButtonTabs } from '~/components/button-tabs/button-tabs';
import { CardItemInfo, ItemCard } from '~/components/item-card/item-card';
import { ItemList } from '~/components/item-list/item-list';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useSteps } from '~/graphql/hooks/use-steps';
import { AccessoriesItem, Category, GroupedAccessories } from '~/graphql/mapper/map-accessories';
import { DATA_TEST_ID } from '~/test/data-test-id';

type TabIds = 'equipment' | 'accessories';

const AccessoriesWrapper = ({
	children,
	shouldShowListDesign,
}: {
	children: React.ReactNode;
	shouldShowListDesign: boolean;
}) => {
	if (shouldShowListDesign) {
		return <StyledAccessoriesWrapper>{children}</StyledAccessoriesWrapper>;
	}
	return children;
};

export const AccessoriesAndEquipments = () => {
	const { i18n } = useI18n();
	const { step, total } = useSteps('accessoriesAndEquipments');
	const scrollAnchor = useRef<HTMLDivElement>(null);

	const { showAccessoriesExclusive, showAccessoriesInclusive } = useMarketUiConfig();
	const { config } = useMarketConfig();
	const shouldShowListDesign = !config.hideStandardAccessories;

	const { accessories, loadingFlags, isItemChecked, isItemLoading, changeConfiguration, isLoading } =
		useConfigurator();
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { feature } = useVATConfig();
	const { getName } = useDebugMode();

	const [currentTab, setCurrentTab] = useState<TabIds | ''>('');
	const [search, setSearch] = useState('');
	const [hadZeroResults, setHadZeroResults] = useState(false);
	const [filter, setFilter] = useState('All');
	const [visibleItemsCount, setVisibleItemsCount] = useState(ACCESSORIES_MIN_VISIBLE_ITEMS);
	const [a11ySearchMessage, setA11ySearchMessage] = useState('');
	const searchInputRef = useRef<HTMLInputElement>(null);

	const isCategorySelected = useCallback(
		(filterId: string) => (search && filterId === 'All') || (!search && filter === filterId),
		[filter, search],
	);

	const scrollToTop = useCallback(() => {
		if (!scrollAnchor || !scrollAnchor.current) return;
		scrollAnchor.current.scrollIntoView({ inline: 'nearest', behavior: 'smooth', block: 'start' });
	}, [scrollAnchor]);

	const tabInformation = useMemo(() => {
		const accessoriesToItems = (
			_accessories: GroupedAccessories,
		): { categories: Array<Category & { active: boolean }> } => {
			const items = Array.from(_accessories.values()).flatMap((category) => category.items);

			const categories = Array.from(_accessories.values()).map((category) => ({
				...category,
				active: false,
				label: category.label,
			}));
			const all = {
				id: 'All',
				label: i18n('ngc.common.all'),
				active: false,
				items,
			};
			return { categories: [all, ...categories] };
		};

		const prepareTabs = new Map<TabIds, { title: string; categories: Category[] }>();
		if (showAccessoriesExclusive && accessories.exclusive.size > 0) {
			prepareTabs.set('equipment', {
				title: i18n('ngc.accessories.headline'),
				...accessoriesToItems(accessories.exclusive),
			});
		}

		if (showAccessoriesInclusive && accessories.inclusive.size > 0) {
			prepareTabs.set('accessories', {
				title: i18n('ngc.equipment.audiOriginalAccessories'),
				...accessoriesToItems(accessories.inclusive),
			});
		}
		return prepareTabs;
	}, [accessories.exclusive, accessories.inclusive, i18n, showAccessoriesExclusive, showAccessoriesInclusive]);

	const stepTitle = useMemo(() => {
		if (tabInformation.size === 1) {
			return tabInformation.entries().next().value?.[1].title || i18n('ngc.equipmentAndAccessories.headline');
		}
		return i18n('ngc.equipmentAndAccessories.headline');
	}, [i18n, tabInformation]);

	const tabs = useMemo(() => {
		return Array.from(tabInformation.keys()).map((key) => ({
			value: key,
			label: tabInformation.get(key)?.title || '',
		}));
	}, [tabInformation]);

	const categories = useMemo(() => {
		if (currentTab === '') return [];
		return (
			tabInformation
				.get(currentTab)
				?.categories.map((category) => ({ ...category, active: isCategorySelected(category.id) })) || []
		);
	}, [currentTab, isCategorySelected, tabInformation]);

	const mapItemToCardInfo = useCallback(
		(item: AccessoriesItem): CardItemInfo => {
			return {
				...item,
				name: htmlToText(getName(item) || ''),
				disabled: !item.status.changeable || loadingFlags.checkingConflicts,
				loading: isItemLoading(item),
				selected: isItemChecked(item),
				standard: item.status.standard,
				hasDetails: showMoreInformationDialog(item),
			};
		},
		[getName, isItemChecked, isItemLoading, loadingFlags.checkingConflicts],
	);

	const getItemCountText = useCallback(
		(visibleCount: number, totalItemsCount: number) => {
			const itemCount =
				totalItemsCount > ACCESSORIES_MIN_VISIBLE_ITEMS
					? visibleCount > totalItemsCount
						? totalItemsCount
						: visibleCount
					: totalItemsCount;

			return i18n('ngc.accessories.itemCount', {
				itemCount,
				totalCount: totalItemsCount,
			});
		},
		[i18n],
	);

	const { displayItems, totalItems, itemsToAdd } = useMemo(() => {
		if (currentTab === '') return { displayItems: [], totalItems: [], itemsToAdd: 0 };
		const items = search
			? categories.find(({ id }) => id === 'All')?.items || []
			: categories.find(({ active }) => active)?.items || [];

		const filteredItems = items.filter(
			(item) =>
				// Search in name and description
				(getName(item) || '').toLowerCase().includes(search.toLowerCase()) ||
				(item.texts?.akText || '').toLowerCase().includes(search.toLowerCase()) ||
				(item.texts?.intro || '').toLowerCase().includes(search.toLowerCase()) ||
				item.texts?.benefits?.some((benefit) => (benefit || '').toLowerCase().includes(search.toLowerCase())) ||
				false,
		);

		if (!filteredItems.length && !hadZeroResults) {
			setHadZeroResults(true);
			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.Content,
				eventName: `${currentTab} - show zero results`,
				label: '',
				value: search,
				category: 'accessoriesAndEquipments',
				elementName: 'tab',
				elementCurrentStep: step,
				elementTotalSteps: total,
			});
		}

		const remainingItems = filteredItems.length - visibleItemsCount;

		return {
			displayItems: filteredItems.slice(0, visibleItemsCount),
			totalItems: !search ? items : filteredItems,
			itemsToAdd:
				remainingItems >= ACCESSORIES_MIN_VISIBLE_ITEMS ? ACCESSORIES_MIN_VISIBLE_ITEMS : remainingItems,
		};
	}, [categories, currentTab, getName, search, visibleItemsCount, step, total]);

	const totalCount = totalItems.length;

	useEffect(() => {
		if (tabInformation.size === 0 || currentTab !== '') return;
		const [key] = tabInformation.entries().next().value || [''];
		setCurrentTab(key);
	}, [currentTab, tabInformation]);

	useEffect(() => {
		setFilter('All');
	}, [currentTab]);

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'accessoriesAndEquipments',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const onItemSelect = useCallback(
		(item: AccessoriesItem) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: 'click to change configuration',
				label: item.name,
				elementName: 'card',
				value: `selection feature app: accessoriesAndEquipments: ${currentTab}`,
				category: 'accessoriesAndEquipments',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected: item.status.selected,
				item,
			});
			changeConfiguration(item);
		},
		[changeConfiguration, currentTab, step, total],
	);

	const onTabClick = useCallback(
		(tab: string) => {
			currentTab !== tab &&
				appTrackingService.trackSelectionClickEvent({
					eventAction: EventAction.Content,
					eventName: `tab navi - click on ${tab}`,
					label: tabs.find((t) => t.value === tab)?.label || '',
					category: 'accessoriesAndEquipments',
					elementName: 'tab',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			setCurrentTab(tab as TabIds);
			scrollToTop();
		},
		[step, total, tabs, currentTab],
	);

	const onSearchFocus = useCallback(() => {
		appTrackingService.trackSelectionClickEvent({
			eventAction: EventAction.Content,
			eventName: `${currentTab} - focus on search field`,
			label: '',
			category: 'accessoriesAndEquipments',
			elementName: 'text field',
			elementCurrentStep: step,
			elementTotalSteps: total,
		});
	}, [step, total, currentTab]);

	useEffect(() => {
		if (searchInputRef.current) {
			const inputNode = searchInputRef.current.childNodes[0].childNodes[1];
			inputNode.addEventListener('focus', onSearchFocus);

			return () => {
				inputNode.removeEventListener('focus', onSearchFocus);
			};
		}
	}, [onSearchFocus, searchInputRef]);

	const onCategoryChange = useCallback(
		(category: SelectionOption, isDropdown = false) => {
			setSearch('');
			setVisibleItemsCount(ACCESSORIES_MIN_VISIBLE_ITEMS);
			setFilter(category.id);
			scrollToTop();

			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.Content,
				eventName: `${currentTab} - click to select category filter`,
				label: category.label,
				category: 'accessoriesAndEquipments',
				elementName: isDropdown ? 'drop down' : 'text link',
				elementCurrentStep: step,
				elementTotalSteps: total,
				pos: `${category.items.length}`,
			});
		},
		[step, total, currentTab, visibleItemsCount],
	);

	const focusNewLoadedItem = useCallback(() => {
		const id = `equipment-checkbox-${totalItems && totalItems[displayItems.length]?.pr7}`;

		requestAnimationFrame(() => {
			const element = document.getElementById(id);
			if (element) {
				element.focus();
			}
		});
	}, [displayItems.length, totalItems]);

	useEffect(() => {
		if (displayItems.length === 0 && search === '' && filter !== 'All') {
			setFilter('All');
		}
	}, [displayItems.length, search, filter]);

	useEffect(() => {
		if (!search) {
			setA11ySearchMessage('');
			return;
		}

		const timeoutId = setTimeout(() => {
			const message = totalCount
				? `${i18n('ngc.a11y.search.found')} ${getItemCountText(visibleItemsCount, totalCount)}`
				: i18n('ngc.equipmentAndAccessories.noResults');
			setA11ySearchMessage(message);
		}, 1000);

		return () => clearTimeout(timeoutId);
	}, [totalCount, i18n, search, visibleItemsCount, getItemCountText]);

	const SkeletonComponent = shouldShowListDesign ? ListSkeleton : CardSkeleton;
	const AccessoriesComponent = shouldShowListDesign ? ItemList : ItemCard;

	return (
		<AccessoriesSelectionStepStyle ref={scrollAnchor} shouldShowListDesign={shouldShowListDesign}>
			<AccessoriesConfigurationStepStyle>
				{}
				<div ref={impressionTrackingRef} className="selection-step" tabIndex={0}>
					<OptionsHeader id={'accessories'} title={stepTitle} step={step} total={total} />
					{isLoading ? (
						<>
							<Skeleton variant="oval" height={'20px'} width={'250px'} margin={['0, 0, 16px ,0']} />
							<Skeleton variant="oval" height={'160px'} />
						</>
					) : (
						<>
							<ButtonTabs setSelectedTab={onTabClick} selectedTab={currentTab} tabs={tabs} />
							<div
								id="search-results-message"
								className="sr-only"
								aria-live="polite"
								aria-atomic="true"
								role="status">
								{a11ySearchMessage}
							</div>
							<SearchField
								ref={searchInputRef}
								className="search-accessories"
								id="search-accessories"
								label={i18n('ngc.accessories.search')}
								clearButton={{
									'aria-label': i18n('ngc.a11y.clear'),
									onClick: () => {
										setSearch('');
										setFilter('All');
									},
								}}
								value={search}
								onChange={(event) => {
									setSearch(event.target.value);
									event.target.value.length < search.length && setHadZeroResults(false);
									setVisibleItemsCount(ACCESSORIES_MIN_VISIBLE_ITEMS);
									scrollToTop();
								}}
								data-testid={DATA_TEST_ID.SELECTION_ACCESSORIES_SEARCH_INPUT}
							/>

							<Categories
								label={i18n('ngc.equipmentAndAccessories.filter.label')}
								selectionOptions={categories}
								onChange={(category) => onCategoryChange(category)}
							/>
						</>
					)}
				</div>
			</AccessoriesConfigurationStepStyle>

			<section className="equipment-container">
				{isLoading ? (
					<SkeletonComponent />
				) : displayItems.length ? (
					<>
						<AccessoriesWrapper shouldShowListDesign={shouldShowListDesign}>
							{displayItems.map((item) => (
								<AccessoriesComponent
									item={mapItemToCardInfo(item)}
									key={`equipment-item-${item.pr7}`}
									onChange={() => onItemSelect(item)}
									feature={feature}
									moreInformation={{
										title: i18n('ngc.actions.moreInfo'),
										onMoreInformation: () => openMoreInformationDialog(item, item),
									}}
									standardPriceText={i18n('ngc.common.standard')}
								/>
							))}
						</AccessoriesWrapper>
						<div className="pagination-items">
							{itemsToAdd > 0 && (
								<TextButton
									className="pagination-button"
									variant="secondary"
									size={'sm'}
									onClick={() => {
										setVisibleItemsCount((prev) => prev + itemsToAdd);
										focusNewLoadedItem();
									}}
									data-testid={DATA_TEST_ID.SELECTION_ACCESSORIES_PAGINATION_BUTTON}>
									{i18n('ngc.accessories.showNextItems', { itemsToAdd })}
								</TextButton>
							)}
							<Text
								tag={'div'}
								variant={{
									type: 'copy',
									order: '2',
									style: 'normal',
								}}>
								{getItemCountText(visibleItemsCount, totalCount)}
							</Text>
						</div>
					</>
				) : (
					<NoResult
						onClearSearch={() => {
							setSearch('');
							setFilter('All');
						}}
					/>
				)}
			</section>
		</AccessoriesSelectionStepStyle>
	);
};
